import {Injectable, OnDestroy} from '@angular/core';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, groupBy, mergeMap} from 'rxjs/operators';
import {TabItemModel} from '../models/tab-item-model';
import {IndexedDBService} from "./indexed-db.service";
import {BuildingModel} from "../models/data-models/building-model";
import {GeneralGatewayModel} from "../models/data-models/general-gateway-model";
import {GeneralMeasurementModel} from "../models/data-models/general-measurement-model";
import {GeneralPrePhotosModel} from "../models/data-models/general-pre-photos-model";
import {GeneralSendingModel} from "../models/data-models/general-sending-model";
import {HeatingCheckModel} from "../models/data-models/heating-check-model";
import {HeatingInformationModel} from "../models/data-models/heating-information-model";
import {PhotosModel} from "../models/data-models/photos-model";
import {SensorsCounterModel} from "../models/data-models/sensors-counter-model";
import {SensorsOtherModel} from "../models/data-models/sensors-other-model";
import {SensorsOutsideTempModel} from "../models/data-models/sensors-outside-temp-model";
import {SensorsPumpModel} from "../models/data-models/sensors-pump-model";
import {SensorsTemperatureModel} from "../models/data-models/sensors-temperature-model";

@Injectable({
  providedIn: 'root'
})
export class AutoSaveService implements OnDestroy {

  public static LOW_IMAGE_QUALITY = 30;

  private dataSubject = new Subject<{ data: any, table: string, key: string }>();

  private _pauseSaving = false;

  private switchOff = false;  // disable saving and loading of data, useful for testing/debugging

  // private inputSubscriptions: Subscription[] = [];
  public currentGeneralSubTab: TabItemModel;  // install gateway tab
  public currentHeatingSubTab: TabItemModel;  // install gateway tab
  public currentSensorsSubTab: TabItemModel;  // install gateway tab

  private debugMode = false;

  private subs: Subscription[];

  constructor(
    private indexedDbService: IndexedDBService
  ) {
    this.init();
  }

  private async init() {
    await this.indexedDbService.initDB();
    this.dataSubject.pipe(
      groupBy(({key}) => key),
      mergeMap(group$ => group$.pipe(debounceTime(300)))
    ).subscribe(({data, table, key}) => this.saveData(data, table, key));
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  /**
   * Emits the gateway ID to be saved through the dataSubject.
   * @public
   * @param {string} gatewayId - The gateway ID to save.
   */
  public saveGatewayId(gatewayId: string): void {
    this.dataSubject.next({
      data: gatewayId,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.GATEWAY_ID
    });
  }

  /**
   * Retrieves the gateway ID from local storage.
   * @public
   * @returns {Promise<string>} The gateway ID.
   */
  public async getGatewayId(): Promise<string> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.GATEWAY_ID
    );
  }

  /**
   * Emits the building data to be saved through the dataSubject.
   * @public
   * @param {BuildingModel} data - The building data to save.
   */
  public saveBuilding(data: BuildingModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.BUILDING
    });
  }

  /**
   * Retrieves the building data from local storage.
   * @public
   * @returns {Promise<BuildingModel>} The building data.
   */
  public async getBuilding(): Promise<BuildingModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.BUILDING
    );
  }

  /**
   * Emits the general gateway data to be saved through the dataSubject.
   * @public
   * @param {GeneralGatewayModel} data - The general gateway data to save.
   */
  public saveGeneralGateway(data: GeneralGatewayModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.GENERAL_GATEWAY
    });
  }

  /**
   * Retrieves the general gateway data from local storage.
   * @public
   * @returns {Promise<GeneralGatewayModel>} The general gateway data.
   */
  public async getGeneralGateway(): Promise<GeneralGatewayModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.GENERAL_GATEWAY
    );
  }

  /**
   * Emits the general measurement data to be saved through the dataSubject.
   * @public
   * @param {GeneralMeasurementModel} data - The general measurement data to save.
   */
  public saveGeneralMeasurement(data: GeneralMeasurementModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.GENERAL_MEASUREMENT
    });
  }

  /**
   * Retrieves the general measurement data from local storage.
   * @public
   * @returns {Promise<GeneralMeasurementModel>} The general measurement data.
   */
  public async getGeneralMeasurement(): Promise<GeneralMeasurementModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.GENERAL_MEASUREMENT
    );
  }

  /**
   * Emits the general pre-photos data to be saved through the dataSubject.
   * @public
   * @param {GeneralPrePhotosModel} data - The general pre-photos data to save.
   */
  public saveGeneralPrePhotos(data: GeneralPrePhotosModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.GENERAL_PRE_PHOTOS
    });
  }

  /**
   * Retrieves the general pre-photos data from local storage.
   * @public
   * @returns {Promise<GeneralPrePhotosModel>} The general pre-photos data.
   */
  public async getGeneralPrePhotos(): Promise<GeneralPrePhotosModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.GENERAL_PRE_PHOTOS
    );
  }

  /**
   * Emits the general sending data to be saved through the dataSubject.
   * @public
   * @param {GeneralSendingModel} data - The general sending data to save.
   */
  public saveGeneralSending(data: GeneralSendingModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.GENERAL_SENDING
    });
  }

  /**
   * Retrieves the general sending data from local storage.
   * @public
   * @returns {Promise<GeneralSendingModel>} The general sending data.
   */
  public async getGeneralSending(): Promise<GeneralSendingModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.GENERAL_SENDING
    );
  }

  /**
   * Emits the heating check data to be saved through the dataSubject.
   * @public
   * @param {HeatingCheckModel} data - The heating check data to save.
   */
  public saveHeatingCheck(data: HeatingCheckModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.HEATING_CHECK
    });
  }

  /**
   * Retrieves the heating check data from local storage.
   * @public
   * @returns {Promise<HeatingCheckModel>} The heating check data.
   */
  public async getHeatingCheck(): Promise<HeatingCheckModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.HEATING_CHECK
    );
  }

  /**
   * Emits the heating information data to be saved through the dataSubject.
   * @public
   * @param {HeatingInformationModel} data - The heating information data to save.
   */
  public saveHeatingInformation(data: HeatingInformationModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.HEATING_INFORMATION
    });
  }

  /**
   * Retrieves the heating information data from local storage.
   * @public
   * @returns {Promise<HeatingInformationModel>} The heating information data.
   */
  public async getHeatingInformation(): Promise<HeatingInformationModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.HEATING_INFORMATION
    );
  }

  /**
   * Emits the photos data to be saved through the dataSubject.
   * @public
   * @param {PhotosModel} data - The photos data to save.
   */
  public savePhotos(data: PhotosModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.PHOTOS
    });
  }

  /**
   * Retrieves the photos data from local storage.
   * @public
   * @returns {Promise<PhotosModel>} The photos data.
   */
  public async getPhotos(): Promise<PhotosModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.PHOTOS
    );
  }

  /**
   * Emits the sensors counter data to be saved through the dataSubject.
   * @public
   * @param {SensorsCounterModel} data - The sensors counter data to save.
   */
  public saveSensorsCounter(data: SensorsCounterModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.SENSORS_COUNTER
    });
  }

  /**
   * Retrieves the sensors counter data from local storage.
   * @public
   * @returns {Promise<SensorsCounterModel>} The sensors counter data.
   */
  public async getSensorsCounter(): Promise<SensorsCounterModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.SENSORS_COUNTER
    );
  }

  /**
   * Emits the sensors other data to be saved through the dataSubject.
   * @public
   * @param {SensorsOtherModel} data - The sensors other data to save.
   */
  public saveSensorsOther(data: SensorsOtherModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.SENSORS_OTHER
    });
  }

  /**
   * Retrieves the sensors other data from local storage.
   * @public
   * @returns {Promise<SensorsOtherModel>} The sensors other data.
   */
  public async getSensorsOther(): Promise<SensorsOtherModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.SENSORS_OTHER
    );
  }

  /**
   * Emits the sensors outside temperature data to be saved through the dataSubject.
   * @public
   * @param {SensorsOutsideTempModel} data - The sensors outside temperature data to save.
   */
  public saveSensorsOutsideTemp(data: SensorsOutsideTempModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.SENSORS_OUTSIDE_TEMP
    });
  }

  /**
   * Retrieves the sensors outside temperature data from local storage.
   * @public
   * @returns {Promise<SensorsOutsideTempModel>} The sensors outside temperature data.
   */
  public async getSensorsOutsideTemp(): Promise<SensorsOutsideTempModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.SENSORS_OUTSIDE_TEMP
    );
  }

  /**
   * Emits the sensors pump data to be saved through the dataSubject.
   * @public
   * @param {SensorsPumpModel} data - The sensors pump data to save.
   */
  public saveSensorsPump(data: SensorsPumpModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.SENSORS_PUMP
    });
  }

  /**
   * Retrieves the sensors pump data from local storage.
   * @public
   * @returns {Promise<SensorsPumpModel>} The sensors pump data.
   */
  public async getSensorsPump(): Promise<SensorsPumpModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.SENSORS_PUMP
    );
  }

  /**
   * Emits the sensors temperature data to be saved through the dataSubject.
   * @public
   * @param {SensorsTemperatureModel} data - The sensors temperature data to save.
   */
  public saveSensorsTemperature(data: SensorsTemperatureModel): void {
    this.dataSubject.next({
      data,
      table: IndexedDBService.Tables.DATA,
      key: IndexedDBService.DataKeys.SENSORS_TEMPERATURE
    });
  }

  /**
   * Retrieves the sensors temperature data from local storage.
   * @public
   * @returns {Promise<SensorsTemperatureModel>} The sensors temperature data.
   */
  public async getSensorsTemperature(): Promise<SensorsTemperatureModel> {
    if (this.switchOff) {
      return;
    }
    return await this.indexedDbService.getData(
      IndexedDBService.Tables.DATA,
      IndexedDBService.DataKeys.SENSORS_TEMPERATURE
    );
  }


  /**
   * Saves the data to IndexedDB.
   * @private
   * @param {any} data - The data to save.
   * @param {string} table - The table name.
   * @param {string} key - The key to use for saving the data.
   */
  private async saveData(data: any, table: string, key: string): Promise<void> {
    if (this._pauseSaving || this.switchOff) {
      console.warn('autosSave saveData is paused or switched off')
      return;
    }
    if (this.debugMode) {
      console.log('saveData', table, key, data);
    }
    try {
      await this.indexedDbService.setData(data, table, key);
    } catch (e) {
      console.error(`Error saving data to ${table}.${key}`, e);
    }
  }

  /**
   * Removes all data from IndexedDB.
   * @public
   */
  public async resetAllData(): Promise<void> {
    if (this.switchOff) {
      console.warn('autosSave resetAllData is switched off')
      return;
    }

    try {
      // Clear all data keys
      for (const key of Object.values(IndexedDBService.DataKeys)) {
        await this.indexedDbService.deleteData(
          IndexedDBService.Tables.DATA,
          key
        );
      }
    } catch (e) {
      console.error('Error resetting data', e);
    }
  }

  public pauseSaving() {
    this._pauseSaving = true;
    console.log('autoSave  paused');

  }

  public resumeSaving() {
    this._pauseSaving = false;
    console.log('autoSave resumed');
  }

  public pauseSaving_MS(milliseconds: number) {
    this._pauseSaving = true;
    setTimeout(() => {
      this.resumeSaving();
    }, milliseconds);
  }

  // In-memory tab state (not persisted to IndexedDB)
  private currentStepId: number;

  saveCurrentStep(currentStepId: number) {
    this.currentStepId = currentStepId;
  }

  loadCurrentStep(): number {
    return this.currentStepId;
  }

  saveCurrentSensorsSubTab(subTab: TabItemModel) {
    this.currentSensorsSubTab = subTab;
  }

  loadCurrentSensorsSubTab(): TabItemModel {
    return this.currentSensorsSubTab;
  }

  saveCurrentHeatingSubTab(subTab: TabItemModel) {
    this.currentHeatingSubTab = subTab;
  }

  loadCurrentHeatingSubTab(): TabItemModel {
    return this.currentHeatingSubTab;
  }

  saveCurrentGeneralSubTab(subTab: TabItemModel) {
    this.currentGeneralSubTab = subTab;
  }

  /**
   * Save all data from IndexedDB with keyPrefix 'debug_'
   */
  async saveDebugData(): Promise<void> {
    try {
      for (const key of Object.values(IndexedDBService.DataKeys)) {
        const value = await this.indexedDbService.getData(
          IndexedDBService.Tables.DATA,
          key
        );
        if (value !== undefined) {
          await this.indexedDbService.setData(
            value,
            IndexedDBService.Tables.DATA,
            'debug_' + key
          );
        }
      }

      console.log('Debug data saved successfully');
    } catch (e) {
      console.error('Error saving debug data:', e);
    }
  }

  /**
   * Replace all data in IndexedDB without keyPrefix 'debug_' with data from keys with keyPrefix 'debug_'
   * If the debug key does not exist, the key without prefix will be removed
   */
  async loadDebugData(): Promise<void> {
    try {
      for (const key of Object.values(IndexedDBService.DataKeys)) {
        const debugValue = await this.indexedDbService.getData(
          IndexedDBService.Tables.DATA,
          'debug_' + key
        );

        await this.indexedDbService.deleteData(
          IndexedDBService.Tables.DATA,
          key
        );

        if (debugValue !== undefined) {
          await this.indexedDbService.setData(
            debugValue,
            IndexedDBService.Tables.DATA,
            key
          );
        }
      }

      console.log('Debug data loaded successfully');
    } catch (e) {
      console.error('Error loading debug data:', e);
    }
  }

  /**
   * Remove all data from IndexedDB with keyPrefix 'debug_'
   */
  async clearDebugData(): Promise<void> {
    try {
      for (const key of Object.values(IndexedDBService.DataKeys)) {
        await this.indexedDbService.deleteData(
          IndexedDBService.Tables.DATA,
          'debug_' + key
        );
      }

      console.log('Debug data cleared successfully');
    } catch (e) {
      console.error('Error clearing debug data:', e);
    }
  }

  get dataSuccessfullySaved(): boolean {
    return !this.indexedDbService.someDataWasNotSaved();
  }

  loadCurrentGeneralSubTab(): TabItemModel {
    return this.currentGeneralSubTab;
  }
}
